import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import { logV } from "../../../shared/services/debug-logger";
import VoyageEvaluationLandingFilters from "../components/VoyageEvaluationLandingFilters";
import VoyageEvaluationSummaryTable from "../components/VoyageEvaluationSummaryTable";
import VoyagePerformanceFilters from "../components/VoyagePerformanceFilters";
import VoyagePerformanceKpiCards from "../components/VoyagePerformanceKpiCards";
import VoyagePerformanceReportTable from "../components/VoyagePerformanceReportTable";
import VoyagePerformanceSummary from "../components/VoyagePerformanceSummary";
import { useVoyageEvaluationDashboard } from "../state/useVoyageEvaluationDashboard";
import { formatVoyageEvaluationDate } from "../utils/voyage-evaluation-selectors.utils";
import { ArrowLeftIcon, CheckBadgeIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

// Replaces the Power BI landing and performance bookmarks with one dashboard-local React flow.
// The route guard is duplicated here so a direct URL cannot expose the preview to other users.
const VoyageEvaluationPage = () => {
  const theme = useAtomValue(themeAtom);
  const state = useVoyageEvaluationDashboard(true);
  logV("VoyageEvaluationPage - BUILD", { view: state.view, loading: state.isDashboardLoading || state.isPerformanceLoading });

  return (
    <div className="flex h-screen" data-test="voyage-evaluation-dashboard-page">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main className="flex-1 overflow-y-auto px-4 pb-6 pt-3 sm:px-6">
          <div className="mx-auto max-w-[1900px] space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-20 text-green dark:bg-teal-900/50">
                  <ClipboardDocumentCheckIcon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-green">
                    Voyage evaluation follower
                  </p>
                  <h1 className="text-xl font-semibold sm:text-2xl">
                    {state.view === "landing" || !state.selectedVoyage
                      ? "Voyage Evaluation"
                      : `Voyage Evaluation - MV ${state.selectedVoyage.vesselName} from ${formatVoyageEvaluationDate(state.selectedVoyage.commenceDate)} to ${formatVoyageEvaluationDate(state.selectedVoyage.completeDate)}`}
                  </h1>
                  {state.view === "performance" && state.selectedVoyage && (
                    <p className="text-xs text-gray-500">
                      IMO {state.selectedVoyage.imo} · Voyage {state.selectedVoyage.voyage}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={twMerge(
                    "rounded-full px-3 py-1 text-xs font-semibold",
                    state.useMockData ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800",
                  )}
                  data-test="voyage-evaluation-source-badge"
                >
                  {state.useMockData ? "Mock data" : "Snowflake data"}
                </span>
                {state.dashboard?.lastRefresh && (
                  <span className="text-[10px] text-gray-500">
                    Loaded {formatVoyageEvaluationDate(state.dashboard.lastRefresh)}
                  </span>
                )}
                {state.view === "landing" ? (
                  <button
                    type="button"
                    onClick={state.openPerformance}
                    data-test="voyage-evaluation-assess-voyage"
                    className="inline-flex items-center gap-2 rounded-lg bg-green px-5 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
                  >
                    <CheckBadgeIcon className="h-5 w-5" />
                    Assess Voyage
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={state.backToLanding}
                    data-test="voyage-performance-back"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs font-semibold hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to summary
                  </button>
                )}
              </div>
            </div>

            {state.dashboardError && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
                role="alert"
                data-test="voyage-evaluation-dashboard-error"
              >
                {state.dashboardError} Use the permitted sidebar mock-data toggle to review the interface without
                Snowflake.
              </div>
            )}
            {state.isDashboardLoading && (
              <div
                className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800"
                data-test="voyage-evaluation-dashboard-loading"
              >
                Loading voyage evaluations…
              </div>
            )}

            {state.dashboard && !state.isDashboardLoading && state.view === "landing" && (
              <>
                <VoyageEvaluationLandingFilters
                  voyages={state.dashboard.voyages}
                  filters={state.landingFilters}
                  setFilter={state.setLandingFilter}
                  clearFilters={state.clearAllFilters}
                />
                <VoyageEvaluationSummaryTable
                  voyages={state.filteredVoyages}
                  selectedVoyage={state.selectedVoyage}
                  onSelect={state.setSelectedVoyage}
                />
              </>
            )}

            {state.dashboard && state.view === "performance" && !state.selectedVoyage && (
              <div
                className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800"
                data-test="voyage-performance-no-selection"
              >
                Select a voyage from the landing table, then return to Performance.
              </div>
            )}

            {state.dashboard &&
              state.view === "performance" &&
              state.selectedVoyage &&
              (state.isPerformanceLoading || (!state.performanceResponse && !state.performanceError)) && (
                <div
                  className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800"
                  data-test="voyage-performance-loading"
                >
                  Loading voyage reports…
                </div>
              )}
            {state.dashboard &&
              state.view === "performance" &&
              state.selectedVoyage &&
              !state.isPerformanceLoading &&
              state.performanceError && (
                <div
                  className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
                  role="alert"
                  data-test="voyage-performance-error"
                >
                  {state.performanceError}
                </div>
              )}

            {state.dashboard &&
              state.view === "performance" &&
              state.selectedVoyage &&
              state.performanceResponse &&
              !state.isPerformanceLoading &&
              !state.performanceError && (
                <div className="grid items-start gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
                  <VoyagePerformanceFilters
                    reports={state.performanceResponse?.reports ?? []}
                    selectedReport={state.selectedPerformanceReport}
                    filters={state.performanceFilters}
                    setFilter={state.setPerformanceFilter}
                    clearFilters={state.clearAllFilters}
                    restoreDefaults={state.restorePerformanceDefaults}
                  />
                  <div className="min-w-0 space-y-4">
                    <div className="grid items-start gap-4 2xl:grid-cols-[440px_minmax(0,1fr)]">
                      <VoyagePerformanceKpiCards voyage={state.selectedVoyage} />
                      <VoyagePerformanceSummary
                        voyage={state.selectedVoyage}
                        reports={
                          state.selectedPerformanceReport ? [state.selectedPerformanceReport] : state.filteredReports
                        }
                      />
                    </div>
                    <VoyagePerformanceReportTable
                      reports={state.filteredReports}
                      selectedReport={state.selectedPerformanceReport}
                      onSelect={state.setSelectedPerformanceReport}
                    />
                  </div>
                </div>
              )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default VoyageEvaluationPage;
